import type { ProductDiscountRule, ProductRow } from '../../../services/billing';

// Discounts - the global rule registry (design: billing Bx discounts).
// Rules live on products; the page is the cross-cutting view: what deals
// exist, who gets them and what's about to expire. Everything here derives
// client-side from the products payload.

export type RuleStatus = 'active' | 'scheduled' | 'ended';

export const ENDING_SOON_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

export interface RuleRow {
    product: ProductRow;
    rule: ProductDiscountRule;
    status: RuleStatus;
    // Days until endsAt, when active and ending within ENDING_SOON_DAYS.
    endingDays: number | null;
}

export type DiscountsSortBy = 'rule' | 'rate' | 'applies' | 'schedule' | 'status' | 'eligible';

export const STATUS_RANK: Record<RuleStatus, number> = { active: 0, scheduled: 1, ended: 2 };

// Display label per status, for the Status filter pill's options and for
// matching a row against the selected labels. `discountColumns.tsx` pairs
// each of these with its badge color for rendering.
export const STATUS_LABEL: Record<RuleStatus, string> = {
    active: 'Active',
    scheduled: 'Scheduled',
    ended: 'Ended'
};

export function ruleStatus(rule: ProductDiscountRule, now: number): RuleStatus {
    if (rule.startsAt && new Date(rule.startsAt).getTime() > now) return 'scheduled';
    if (rule.endsAt && new Date(rule.endsAt).getTime() <= now) return 'ended';
    return 'active';
}

export function buildRows(products: ProductRow[], now: number): RuleRow[] {
    return products.flatMap((product) => {
        return product.discountRules.map((rule) => {
            const status = ruleStatus(rule, now);
            const endsIn = rule.endsAt
                ? Math.ceil((new Date(rule.endsAt).getTime() - now) / DAY_MS)
                : null;
            return {
                product,
                rule,
                status,
                endingDays:
                    status === 'active' && endsIn !== null && endsIn <= ENDING_SOON_DAYS
                        ? endsIn
                        : null
            };
        });
    });
}

// The version label a rule is scoped to, as the Applies-to column shows it;
// empty string = 'All versions' (sorted first).
export function appliesLabel(row: RuleRow): string {
    if (!row.rule.appliesToVersionId) return '';
    return row.product.versions.find((v) => v.id === row.rule.appliesToVersionId)?.label ?? '';
}

export function compareRows(a: RuleRow, b: RuleRow, sortBy: DiscountsSortBy): number {
    switch (sortBy) {
        case 'rate': {
            // Percentages group before flats, then by size within the type.
            if (a.rule.discountType !== b.rule.discountType) {
                return a.rule.discountType === 'percentage' ? -1 : 1;
            }
            return a.rule.discountValue - b.rule.discountValue;
        }
        case 'applies':
            return (
                appliesLabel(a).localeCompare(appliesLabel(b)) ||
                a.product.name.localeCompare(b.product.name)
            );
        case 'schedule': {
            // Sort by the next relevant boundary; unscheduled ('Always') rules last.
            const keyOf = (row: RuleRow) => row.rule.endsAt ?? row.rule.startsAt;
            const aKey = keyOf(a);
            const bKey = keyOf(b);
            if (aKey === null || bKey === null)
                return Number(aKey === null) - Number(bKey === null);
            return new Date(aKey).getTime() - new Date(bKey).getTime();
        }
        case 'status':
            return STATUS_RANK[a.status] - STATUS_RANK[b.status];
        case 'eligible':
            return a.rule.eligibleCount - b.rule.eligibleCount;
        default:
            return (
                a.rule.name.localeCompare(b.rule.name) ||
                a.product.name.localeCompare(b.product.name)
            );
    }
}
